import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type React from 'react';
import type { Tender } from '../../../types/tender';
import { EstimatedCostChip } from '../../common/EstimatedCostChip';
import {
  displayDaysUntilDeadline,
  formatDisplayId,
  getTenderSourceString,
  isTenderClosed,
  parseSubmissionDeadlineInTable,
} from './tableUtils';

interface PublicTenderDetailsDialogProps {
  open: boolean;
  tender: Tender | null;
  onClose: () => void;
  onAddTender: (tender: Tender) => void;
  onArchive: (tender: Tender) => void;
  isAdding: boolean;
  isLoading: boolean;
  isPursued: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: Pricing settings
  pricingSettings: any;
}

const PublicTenderDetailsDialog: React.FC<PublicTenderDetailsDialogProps> = ({
  open,
  tender,
  onClose,
  onAddTender,
  onArchive,
  isAdding,
  isLoading,
  isPursued,
  pricingSettings,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  if (!tender) {
    return null;
  }

  const primaryButtonStyle = {
    backgroundColor: theme.palette.greyLight,
    color: `${theme.palette.text.primary} !important`,
    fontWeight: 'bold',
    fontSize: '1.0rem',
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    border: 'none',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.greyContrast,
      boxShadow: theme.shadows[4],
      color: `${theme.palette.primary.contrastText} !important`,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  };

  const secondaryButtonStyle = {
    backgroundColor: theme.palette.bidlogiq.brick,
    color: `${theme.palette.common.white} !important`,
    fontSize: '1.0rem',
    fontWeight: 'bold',
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    border: 'none',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.bidlogiq.brick,
      boxShadow: theme.shadows[4],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  };

  const deadlineDateObj = parseSubmissionDeadlineInTable(tender.submissionDeadline || '');
  const closed = isTenderClosed(deadlineDateObj);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: theme.palette.greyLight,
          color: theme.palette.text.primary,
          borderRadius: 3,
          border: `3px solid ${theme.palette.bidlogiq.brick}`,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(2px)',
            backgroundColor: theme.palette.action.disabledBackground,
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: `1px solid ${theme.palette.greyContrast}` }}>
        {tender.title || 'Tender Details'}
        {tender.noFiles && (
          <Chip
            label="No Docs, Visit Portal"
            component="a"
            href={tender.tenderSourceURL || ''}
            target="_blank"
            clickable
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 'bold',
              ml: 1,
              verticalAlign: 'middle',
              mt: -0.5,
              backgroundColor: theme.palette.greyContrast,
              color: theme.palette.primary.contrastText,
              borderColor: theme.palette.bidlogiq.orange,
              borderWidth: 2,
              '&&:hover': {
                backgroundColor: theme.palette.bidlogiq.brick,
                color: '#fff',
                borderColor: theme.palette.bidlogiq.brick,
              },
            }}
          />
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: theme.palette.greyLight }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <DialogContentText component="div">
          <Typography
            variant="body2"
            sx={{ fontSize: '1.1rem', color: theme.palette.text.primary }}
            gutterBottom
          >
            <strong>Issued by:</strong> {tender.tenderIssuer || 'IssuerMissing'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: '1.1rem', color: theme.palette.text.primary }}
            gutterBottom
          >
            <strong>Closing:</strong>{' '}
            {deadlineDateObj
              ? deadlineDateObj.toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'N/A'}{' '}
            ({displayDaysUntilDeadline(deadlineDateObj)})
          </Typography>
          {(tender.tenderId || tender.opportunityID) && (
            <Typography
              variant="body2"
              sx={{ fontSize: '1.1rem', color: theme.palette.text.primary }}
              gutterBottom
            >
              <strong>Tender ID:</strong>{' '}
              <a
                href={tender.tenderSourceURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  textDecoration: 'underline',
                }}
              >
                {formatDisplayId(tender.tenderId || tender.opportunityID || tender.id)}
                {getTenderSourceString(tender.tenderId || tender.opportunityID || tender.id)
                  ? ` (${getTenderSourceString(tender.tenderId || tender.opportunityID || tender.id)})`
                  : ''}
              </a>
            </Typography>
          )}
          {tender.noFiles && (
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                mt: 2,
                mb: 1,
              }}
            >
              Note: This is a notice-only tender. You must follow the{' '}
              {tender.tenderSourceURL ? (
                <a
                  href={tender.tenderSourceURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  link to the original source page
                </a>
              ) : (
                'link to the original source page'
              )}{' '}
              to retrieve or access the full tender context and documents.
            </Typography>
          )}
          {tender.scope && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ fontSize: '1.1rem', color: theme.palette.text.primary, mt: 2, mb: 0.5 }}
              >
                <strong>Scope:</strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '1.1rem',
                  whiteSpace: 'pre-wrap',
                  p: 1,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                }}
              >
                {tender.scope}
              </Typography>
            </>
          )}

          {tender.keywords &&
            (Array.isArray(tender.keywords)
              ? tender.keywords
              : String(tender.keywords).split(',')
            ).filter((kw) => kw.trim()).length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ color: 'text.primary', mt: 2, mb: 0.5 }}>
                  Keywords:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(Array.isArray(tender.keywords)
                    ? tender.keywords
                    : String(tender.keywords)
                        .split(',')
                        .map((kw) => kw.trim())
                  )
                    .map((kw) => String(kw).replace(/_/g, ' ').replace(/\s+/g, ' ').trim())
                    .filter((kw) => kw)
                    .map((keyword, index) => (
                      <Chip
                        // biome-ignore lint/suspicious/noArrayIndexKey: Order is stable for display
                        key={index}
                        label={keyword}
                        size="small"
                        sx={{
                          fontSize: '.8rem',
                          backgroundColor: theme.palette.bidlogiq.taupe,
                          color: theme.palette.common.black,
                          fontWeight: '600',
                        }}
                      />
                    ))}
                </Box>
              </>
            )}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.palette.greyContrast}`,
          p: '12px 24px',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={primaryButtonStyle}>
            Close
          </Button>
          <Button
            onClick={() => {
              onAddTender(tender);
              onClose();
            }}
            sx={
              isAdding
                ? {
                    ...secondaryButtonStyle,
                    backgroundColor: theme.palette.greyLight,
                    color: theme.palette.greyContrast,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '.MuiButton-startIcon .MuiCircularProgress-root': {
                      color: theme.palette.bidlogiq?.primaryOrange,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.greyLight,
                      color: theme.palette.greyContrast,
                    },
                  }
                : secondaryButtonStyle
            }
            startIcon={
              isAdding ? (
                <CircularProgress size={20} sx={{ color: theme.palette.bidlogiq?.primaryOrange }} />
              ) : (
                <AddIcon />
              )
            }
            disabled={isPursued || closed || isAdding || isLoading}
          >
            {isPursued
              ? 'Already Pursued'
              : closed
                ? 'Closed'
                : isAdding
                  ? 'Adding...'
                  : isSmallScreen
                    ? 'Add'
                    : 'Add to Go/No-Go'}
          </Button>
          <Tooltip
            title="Exact billing depends on the length and complexity of the tender documents and provided context."
            slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
          >
            <EstimatedCostChip
              estimate={pricingSettings?.estimates?.capability_analysis || 'Est. $20.00 – $60.00'}
              sx={{ mt: 1 }}
            />
          </Tooltip>
        </Box>
        <IconButton
          onClick={() => {
            onArchive(tender);
            onClose();
          }}
          disabled={isLoading}
          title="Hide Tender from Store"
          sx={{
            color: theme.palette.secondary.dark,
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.dark, 0.1),
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};

export default PublicTenderDetailsDialog;
